import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { InlineAlert } from '../../components/ui/InlineAlert';
import {
  MyPerformanceInbox,
  MyPerformanceTask,
  fetchMyPerformanceInbox,
} from '../../services/peoplePerformanceApi';

const TASK_KIND_LABELS: Record<string, string> = {
  SelfEvaluation: 'Autoavaliação',
  ManagerEvaluation: 'Avaliação do gestor',
  PeerReview: 'Avaliação 360',
  CycleFeedback: 'Feedback do ciclo',
};

function EmptySection({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="erp-card">
      <div className="erp-card-body space-y-2">
        <h2 className="font-semibold text-secondary-900">{title}</h2>
        <p className="text-sm text-secondary-500">{description}</p>
        <InlineAlert>Nenhum registro disponível ainda.</InlineAlert>
      </div>
    </section>
  );
}

export function MyPerformancePage() {
  const [inbox, setInbox] = useState<MyPerformanceInbox | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        setInbox(await fetchMyPerformanceInbox());
      } catch {
        toast.error('Falha ao carregar tarefas de desempenho.');
        setInbox({ evaluationWindowActive: false, feedbackWindowActive: false, tasks: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tasks = inbox?.tasks ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Desempenho"
        description="Formulários ativos no período de coleta e histórico do seu ciclo de desempenho."
      />

      <div className="space-y-4">
        <section className="erp-card">
          <div className="erp-card-body space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold text-secondary-900">Formulários em coleta</h2>
              <p className="text-xs text-secondary-500">
                {inbox?.evaluationWindowActive ? 'Janela de avaliações aberta' : 'Sem coleta de avaliações agora'}
                {' · '}
                {inbox?.feedbackWindowActive ? 'Janela de feedbacks aberta' : 'Sem coleta de feedbacks agora'}
              </p>
            </div>

            {loading ? (
              <p className="text-sm text-secondary-500">Carregando...</p>
            ) : tasks.length === 0 ? (
              <InlineAlert>
                Não há formulários pendentes para você neste momento.
              </InlineAlert>
            ) : (
              <ul className="divide-y divide-secondary-100 rounded-lg border border-secondary-200">
                {tasks.map((task: MyPerformanceTask) => (
                  <li key={`${task.kind}-${task.calendarId ?? ''}-${task.evaluationId ?? task.peerReviewId ?? task.cycleId}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                    <div>
                      <p className="font-medium text-secondary-900">
                        {TASK_KIND_LABELS[task.kind] ?? task.kind}
                        {task.subjectUserName ? ` — ${task.subjectUserName}` : ''}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {task.cycleName}
                        {task.calendarName ? ` · ${task.calendarName}` : ''}
                        {' · até '}
                        {new Date(task.windowEndUtc).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <Link to={task.formPath} className="erp-btn-primary text-sm">
                      Abrir formulário de coleta
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <EmptySection
          title="Avaliações finalizadas"
          description="Autoavaliação, avaliação do gestor, avaliação 360° e demais ciclos concluídos."
        />
        <EmptySection
          title="Feedbacks registrados"
          description="Feedbacks formais recebidos e registrados ao longo do período."
        />
        <EmptySection
          title="Avaliação final de metas"
          description="Resultado consolidado das metas ao encerramento do ciclo."
        />
      </div>
    </PageShell>
  );
}
